import { useCallback, useEffect, useMemo, useState } from "react";
import * as finService from "../../../services/finService";

// The ledgers and charts of accounts every GL screen needs, plus the ledger the person chose
// (remembered in the browser). Ledgers point at their chart by code: ids are re-encrypted in
// every API response, so they cannot be compared across calls.
const KEY = "finLedgerCode";
const remembered = () => {
  try {
    return localStorage.getItem(KEY);
  } catch {
    return null;
  }
};

export default function useGlContext() {
  const [state, setState] = useState({ loading: true, error: null, ledgers: [], structures: [] });
  const [code, setCode] = useState(remembered());

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const [l, st] = await Promise.all([finService.listLedgers(), finService.listStructures()]);
      if (l?.success === false || st?.success === false) throw new Error(l?.message || st?.message);
      setState({ loading: false, error: null, ledgers: l?.data || [], structures: st?.data || [] });
    } catch (err) {
      setState({ loading: false, error: err.message || "Something went wrong", ledgers: [], structures: [] });
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const ledger = useMemo(() => state.ledgers.find((l) => l.code === code) || state.ledgers[0] || null, [state.ledgers, code]);
  const structure = useMemo(() => (ledger ? state.structures.find((s) => s.code === ledger.structure_code) || null : state.structures[0] || null), [ledger, state.structures]);

  const chooseLedger = (c) => {
    setCode(c);
    try {
      localStorage.setItem(KEY, c);
    } catch {
      /* storage blocked: the choice lasts until reload */
    }
  };

  return { ...state, ledger, structure, chooseLedger, reload: load, ledgerOptions: state.ledgers.map((l) => ({ label: `${l.code} – ${l.name} (${l.currency})`, value: l.code })) };
}

// "1,234.50" in a ledger currency.
export const fmtMoney = (n, digits = 2) =>
  n === null || n === undefined || n === "" ? "" : Number(n).toLocaleString("en-US", { minimumFractionDigits: digits, maximumFractionDigits: digits });

// Debit / credit columns from a signed amount.
export const drCr = (n) => (Number(n) >= 0 ? [Number(n) || 0, 0] : [0, -Number(n)]);
